import React, { useState, useEffect, useRef } from "react"
import * as layouts from "layouts"
import * as sessions from "sessions"
import * as metasearch from "metasearch"
import * as uuid from "uuid"
import * as brands from "brands"
import * as api from "./api"
import * as httpx from "httpx"
import * as errors from "errors"
import * as timex from "timex"
import { card as Card } from "./metric.card"
import { SearchRequest, SearchResponse } from "./brandguard.metrics_pb"
import * as chart from "./chart"
import * as icons from "icons"
import * as inputs from "inputs"
import * as metricsLayouts from "./layouts"
import * as filters from "./filters"
import { getDaysArray, toDayKey, INFISO, NEGINFISO } from "./dates"

interface parsed {
	uploads: chart.metric
}

function parsedInitial(proto: Partial<parsed> = {}): parsed {
	return {
		uploads: chart.parsedMetricZero({
			title: "Uploads",
			chart: chart.parsedChartZero({
				axisID: "y",
				axisName: "Count",
				borderColor: layouts.theme.colors.charts.yellow.base,
				backgroundColor: layouts.theme.colors.charts.yellow.alpha,
			}),
		}),
		...proto,
	}
}

function parse(uploads: SearchResponse, dr: metasearch.DateRange, uid: string = uuid.NIL): parsed {
	const data = parsedInitial()

	getDaysArray(new Date(dr.oldest), new Date(dr.newest)).forEach((i) => {
		const day = toDayKey(i)
		data.uploads.chart.labels.push(day)
		Object.assign(data.uploads.chart.data, { [day]: 0 })
	})
	;(uploads.items || []).forEach((e) => {
		if (uid !== uuid.NIL && e.profile_id !== uid) return
		const day = toDayKey(new Date(e.range!.oldest))
		data.uploads.val += Number(BigInt(e.approved || 0))
		data.uploads.chart.data[day] += Number(BigInt(e.approved || 0))
	})

	return data
}

export default function Display(): JSX.Element {
	const brand = brands.caching.useCached()
	const bearertoken = sessions.useToken()

	const [loading, setLoading] = useState(true)
	const [cause, setCause] = useState(undefined as JSX.Element | undefined)

	const [req, setReq] = useState(
		api.SearchZero({
			brand_id: brand.id,
			range: metasearch.dateranges.days(30),
		}) as SearchRequest,
	)

	const [chartData, setChartData] = useState(parsedInitial({}))
	const [currentUser, setCurrentUser] = useState(filters.zeroUser)
	const [uploadsResponse, setUploadsResponse] = useState({} as SearchResponse)
	const currentUserRef = useRef(currentUser)
	currentUserRef.current = currentUser

	const updateCurrentUser = (user: filters.user): void => {
		setCurrentUser(user)
		setChartData(parse(uploadsResponse, req.range!, user.key))
	}

	useEffect(() => {
		if (req.brand_id === uuid.NIL || req.range?.oldest === NEGINFISO || req.range?.newest === INFISO) return
		setLoading(true)
		const retry = httpx.autoretry()
		const p = retry
			.wrap(() => api.search_uploads(req, bearertoken))
			.then((uploadsResp) => {
				setUploadsResponse(uploadsResp)
				setChartData(parse(uploadsResp, req.range!, currentUserRef.current.key))
				setCause(undefined)
			})
			.catch(httpx.errors.cancellation(console.warn))
			.catch(
				httpx.errors.forbidden((cause) => {
					console.warn("insufficient privileges unable to view metrics", cause)
					setCause(
						<layouts.containers.box styled m="auto">
							<errors.Textual>you do not have permission to view metrics</errors.Textual>
						</layouts.containers.box>,
					)
				}),
			)
			.catch((c: unknown) => {
				console.error("unable to retrieve metrics", c)
				setCause(
					<layouts.containers.box styled m="auto" width="100%" height="100%">
						<errors.Textual onClick={() => setCause(undefined)}>unable to retrieve metrics</errors.Textual>
					</layouts.containers.box>,
				)
			})
			.finally(() => setLoading(false))
		return p.cancel
	}, [req])

	return (
		<layouts.loading.screen loading={loading} height="100%">
			<errors.overlay cause={cause} flexDirection="column" flex="1">
				<layouts.containers.flex>
					<inputs.calendars.DateRangeOneDatepicker
						className={metricsLayouts.StyledDatePicker}
						placeholderText="Date Range"
						minStartDate={new Date(timex.from.iso(req.range?.oldest).minus(timex.duration.days(180)).toISO())}
						maxEndDate={new Date()}
						startDate={new Date(req.range?.oldest || NEGINFISO)}
						endDate={new Date(req.range?.newest || INFISO)}
						changeStart={(d: Date) => {
							setReq((prevState) => ({ ...prevState, range: { newest: INFISO, oldest: timex.from.date(d).toISO() } }))
						}}
						changeEnd={(d: Date) => {
							setReq((prevState) => ({
								...prevState,
								range: { ...prevState.range, newest: timex.from.date(d).toISO() },
							}))
						}}
					/>

					<layouts.containers.flex mt="3px" ml="10px" fontWeight="600">
						{req.range?.oldest !== NEGINFISO &&
							req.range?.newest !== INFISO &&
							`${timex.from
								.iso(req.range?.newest)
								.diff(timex.from.iso(req.range?.oldest), "days")
								.days.toFixed()} day(s)`}
					</layouts.containers.flex>
					<filters.ApprovalFilter
						req={req}
						setReq={setReq}
						currentUser={currentUser}
						setCurrentUser={updateCurrentUser}
					/>
				</layouts.containers.flex>

				<layouts.containers.box
					className="metrics"
					width="100%"
					background={layouts.theme.colors.white}
					overflowY="auto"
				>
					<chart.Chart labels={chartData.uploads.chart.labels} dataset={[chartData.uploads]} />
					<layouts.containers.flex justifyContent="center">
						<Card
							icon={
								<icons.ImpressionsEye
									fill={chartData.uploads.chart.borderColor}
									width="30px"
									height="30px"
									verticalAlign="middle"
								/>
							}
							data={chartData.uploads}
						/>
					</layouts.containers.flex>
				</layouts.containers.box>
			</errors.overlay>
		</layouts.loading.screen>
	)
}
